import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {show} from "redux-modal";
import {showFile} from "../../../../actions/filesActions";
import {removeTaskUploadStatus} from "../../../../actions/taskActions";
import {downloadFile} from "../../../../api/files/downloadFile";
import {getFile} from "../../../../api/files/getFile";
import {getFileObject as getFileObjectAPI} from "../../../../api/files/getFileObject";
import {
    removeFile,
    removeFileNotificationFailure,
    removeFileNotificationSuccess,
} from "../../../../api/files/removeFile";
import {
    isFileSharingSupported,
    shareFile,
} from "../../../../api/files/shareFile";
import {BreakPoint} from "../../../../constans/breakpointTypes";
import {useOnScreen} from "../../../../hooks/useOnScreen";
import useWindowSize from "../../../../hooks/useWindowSize";
import {
    canShowThumbnail,
    getFileExtension,
    getFileObjectName,
} from "../../../../utils/FileUtils";
import {isMobile} from "../../../../utils/MobileUtils";
import {ModalName as ConfirmModalName} from "../../../modals-new/confirm-modal/ConfirmModal";
import DesktopCalendarTaskDrawerFileMenu from "./DesktopCalendarTaskDrawerFileMenu";
import DrawerFileTile from "./DrawerFileTile";

function DesktopCalendarTaskDrawerFileItem({
    file,
    drawer,
    FarmID,
    filesForPreview = [],
    isMarkedForRemove = false,
    onTaskFileRemoveToggle,
    // wyniki wyszukiwania pokazuja ten sam kafelek, ale plik da sie stamtad
    // tylko obejrzec - usuwanie zostaje w drawerze zadania
    readOnly = false,
}) {
    const dispatch = useDispatch();
    const [fileObject, setFileObject] = useState(null);
    const [thumbnail, setThumbnail] = useState(null);
    const [menu, setMenu] = useState(null);
    const [busyAction, setBusyAction] = useState(null);
    const {FileID} = file;
    const container = useRef();
    const isMounted = useRef(true);
    const {t} = useTranslation();
    const isOnScreen = useOnScreen(container, drawer?.current);
    const windowSize = useWindowSize();
    // o wariancie decyduje szerokosc okna, a nie urzadzenie - na waskim
    // desktopie kafelek tez ma dostac kropki zamiast akcji na hover
    const compact = windowSize.width <= BreakPoint.SM;

    const isImage = useCallback((item) => {
        if (!item) return false;
        const type = (item.Type || "").toLowerCase();
        if (type.includes("image")) return true;

        const fileName = item.FileName || item.Name || item.OriginalName || "";
        const extension = fileName.split(".").pop()?.toLowerCase();
        const mediaExtensions = ["jpg", "jpeg", "png", "webp", "bmp", "gif"];

        return mediaExtensions.includes(extension);
    }, []);

    const previewFiles = useMemo(() => {
        if (!filesForPreview?.length) return [];
        const tmp = [];
        for (let file of filesForPreview) {
            if (isImage(file)) {
                tmp.push({
                    ...file,
                    FarmID: file?.FarmID || FarmID,
                });
            }
        }
        return tmp;
    }, [filesForPreview, FarmID, isImage]);

    const buildPreviewPayload = useCallback(
        (targetFile) => {
            if (!targetFile) return null;

            const list = previewFiles.length
                ? previewFiles
                : [{...targetFile, FarmID: targetFile?.FarmID || FarmID}];
            const currentIndex = list.findIndex(
                (item) => item.FileID === targetFile.FileID
            );

            const getAdjacent = (step) => {
                if (!list.length) return null;
                if (currentIndex === -1) return list[0];
                const nextIndex =
                    (currentIndex + step + list.length) % list.length;
                return list[nextIndex];
            };

            return {
                ...targetFile,
                FarmID: targetFile?.FarmID || FarmID,
                onClickPrevious: () => {
                    const previous = getAdjacent(-1);
                    if (previous) {
                        dispatch(showFile(buildPreviewPayload(previous)));
                    }
                },
                onClickNext: () => {
                    const next = getAdjacent(1);
                    if (next) {
                        dispatch(showFile(buildPreviewPayload(next)));
                    }
                },
            };
        },
        [previewFiles, FarmID, dispatch]
    );

    // plik da sie usunac w trakcie pobierania - stan konczy sie wtedy na
    // odmontowanym kafelku
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (!isOnScreen || fileObject) return;

        async function getFileObject() {
            let res = await getFileObjectAPI(FileID, {FarmID});
            setFileObject(res.file);
            setThumbnail(res.thumbnail);
        }

        getFileObject();
    }, [isOnScreen, FileID, fileObject, FarmID]);

    const onPreviewClick = useCallback(async () => {
        if (!fileObject) return;

        const image = isImage(fileObject);

        if (isMobile()) {
            if (image) {
                dispatch(showFile(buildPreviewPayload(fileObject)));
                return;
            }

            const newWindow = window.open("", "_blank");
            try {
                const url = await getFile(fileObject.FileID, {
                    FarmID,
                    disposition: "inline",
                });
                if (newWindow) {
                    newWindow.location.href = url;
                } else {
                    window.location.href = url;
                }
            } catch (err) {
                if (newWindow) newWindow.close();
                downloadFile(fileObject);
            }
            return;
        }

        if (image) {
            dispatch(showFile(buildPreviewPayload(fileObject)));
            return;
        }

        dispatch(showFile(fileObject));
    }, [fileObject, dispatch, FarmID, buildPreviewPayload, isImage]);

    // kafelek potrafi siedziec w klikalnym wierszu (wyniki wyszukiwania) -
    // otwarcie podgladu nie moze przy okazji odpalic tego, co jest wyzej.
    // Blokada wisi na samym podgladzie, a nie na kontenerze: gdy pliku nie da
    // sie podejrzec, kafelek nie ma onClick i klik ma prawo polecieć dalej,
    // zamiast konczyc sie niczym
    const onItemClick = useCallback(
        (event) => {
            event?.stopPropagation?.();
            onPreviewClick();
        },
        [onPreviewClick]
    );

    const onRemoveClick = useCallback(() => {
        if (onTaskFileRemoveToggle) {
            onTaskFileRemoveToggle(fileObject || file);
            return;
        }

        dispatch(
            show(ConfirmModalName, {
                title: t("removeFile", {name: fileObject.FileName}),
                text: t("removeFileText", {name: fileObject.FileName}),
                confirmText: t("yes"),
                onConfirmed: (props) => {
                    return removeFile(fileObject.FileID, {FarmID}, false)
                        .then((res) => {
                            props.handleHide();
                            dispatch(
                                removeTaskUploadStatus(
                                    fileObject.FileID,
                                    fileObject.IssueID
                                )
                            );
                            removeFileNotificationSuccess(res);
                        })
                        .catch((e) => {
                            removeFileNotificationFailure(e);
                        });
                },
            })
        );
    }, [dispatch, fileObject, t, FarmID, onTaskFileRemoveToggle, file]);

    // pobranie i udostepnienie najpierw musza wyciagnac podpisany link z S3,
    // wiec akcja trwa - kafelek pokazuje spinner na przycisku, ktory ja odpalil
    const runFileAction = useCallback(
        async (key, action) => {
            if (!fileObject || busyAction) return;
            setBusyAction(key);
            try {
                await action(fileObject);
            } catch (e) {
                // powiadomienie o bledzie leci z samej akcji
            } finally {
                if (isMounted.current) setBusyAction(null);
            }
        },
        [fileObject, busyAction]
    );

    const onDownloadClick = useCallback(() => {
        runFileAction("download", downloadFile);
    }, [runFileAction]);

    // te same handlery obsluguja przyciski na kafelku i pozycje w menu -
    // menu renderuje sie w portalu, ale klik z niego leci w gore drzewa
    // Reacta, wiec bez stopPropagation odpalilby tez klikalny wiersz wyzej
    // (wyniki wyszukiwania) i przy okazji otwieral zadanie
    const onDownloadButtonClick = useCallback(
        (e) => {
            e?.stopPropagation?.();
            onDownloadClick();
        },
        [onDownloadClick]
    );

    const onShareClick = useCallback(() => {
        runFileAction("share", shareFile);
    }, [runFileAction]);

    const onShareButtonClick = useCallback(
        (e) => {
            e?.stopPropagation?.();
            onShareClick();
        },
        [onShareClick]
    );

    const onRemoveButtonClick = useCallback(
        (e) => {
            e?.stopPropagation?.();
            onRemoveClick();
        },
        [onRemoveClick]
    );

    const onDotsClick = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setMenu(e);
    }, []);

    const onMenuClose = useCallback(() => setMenu(null), []);

    const canUseThumbnail = canShowThumbnail(
        getFileExtension(getFileObjectName(fileObject))
    );

    const canShowPreview =
        fileObject &&
        (isMobile() ||
            fileObject.Type?.includes("image") ||
            fileObject.Type?.includes("video") ||
            fileObject.Type?.includes("pdf"));

    const canShare = isFileSharingSupported();

    const name = fileObject?.FileName || file.Name || "";
    const date = fileObject?.DtaCrtTime || file.DtaCrtTime;

    const actions = compact ? (
        <button
            type="button"
            className="drawer-file-tile-btn dots"
            onClick={onDotsClick}
            data-testid="file-tile-dots">
            <i
                className={`fa-solid ${
                    busyAction ? "fa-spinner fa-spin" : "fa-ellipsis"
                }`}
            />
        </button>
    ) : (
        <>
            {/* na desktopie podglad otwiera sam kafelek, wiec przycisk byłby
            drugim wejsciem do tego samego */}
            <button
                type="button"
                className="drawer-file-tile-btn download"
                onClick={onDownloadButtonClick}
                disabled={!fileObject || !!busyAction}
                title={t("newReports.headers.download")}>
                <i
                    className={`fa-solid ${
                        busyAction === "download"
                            ? "fa-spinner fa-spin"
                            : "fa-download"
                    }`}
                />
            </button>
            {canShare && (
                <button
                    type="button"
                    className="drawer-file-tile-btn share"
                    onClick={onShareButtonClick}
                    disabled={!fileObject || !!busyAction}
                    title={t("share")}>
                    <i
                        className={`fa-solid ${
                            busyAction === "share"
                                ? "fa-spinner fa-spin"
                                : "fa-share-nodes"
                        }`}
                    />
                </button>
            )}
            {!readOnly && (
                <button
                    type="button"
                    className="drawer-file-tile-btn remove"
                    onClick={onRemoveButtonClick}
                    title={isMarkedForRemove ? t("cancel") : t("delete")}>
                    <i
                        className={`fa-solid ${
                            isMarkedForRemove ? "fa-rotate-left" : "fa-trash"
                        }`}
                    />
                </button>
            )}
        </>
    );

    return (
        <>
            <DrawerFileTile
                containerRef={container}
                name={name}
                type={fileObject?.Type || ""}
                thumbnail={canUseThumbnail ? thumbnail : null}
                date={date}
                marked={isMarkedForRemove}
                compact={compact}
                loading={!fileObject}
                onClick={canShowPreview ? onItemClick : undefined}
                actions={actions}
            />
            {compact && (
                <DesktopCalendarTaskDrawerFileMenu
                    menu={menu}
                    fileObject={fileObject}
                    onRemoveClick={onRemoveButtonClick}
                    onPreviewClick={onItemClick}
                    onShareClick={canShare ? onShareButtonClick : undefined}
                    onDownloadClick={onDownloadButtonClick}
                    canShowPreview={!!canShowPreview}
                    isMarkedForRemove={isMarkedForRemove}
                    readOnly={readOnly}
                    closeCallback={onMenuClose}
                />
            )}
        </>
    );
}

export default React.memo(DesktopCalendarTaskDrawerFileItem);
